import React, { useState } from "react";
import "./css/Hero.css";
import { useUser } from "./context/UserContext"; // Import UserContext
import useFeatureAccess from "../../hooks/useFeatureAccess"; // Import feature access hook
import { useTranslation } from "../../i18n/LanguageContext";
import BackButton from "./common/BackButton"; // Back navigation button
import SimulationProfileSettings from "./simulation/SimulationProfileSettings"; // Import the SimulationProfileSettings component
import Dashboard from "./dashboard/Dashboard";
import SendEmail from "./simulation/SendEmail"; // Import the SendEmail component
import SimulationHeuristics from "./simulation/SimulationHeuristics"; // Import the Heuristics component
import Training from "./training/Training"; // Import the Training component for security awareness training
import RecentSimulations from "./simulation/RecentSimulations"; // Import the RecentSimulations component
import UserEmailReport from "./reports/UserEmailReport"; // Import the UserEmailReport component
import EmailReportGenerator from "./reports/EmailReportGenerator"; // Import the EmailReportGenerator component
import RiskScoresGenerator from "./reports/RiskScoresGenerator"; // Import the GenerateRiskScores component
import SubscriptionPayment from "./subscriptions/SubscriptionPayment.js"; // Unified Subscription & Payment page
import AccountProfile from "./accounts/AccountProfile"; // Account owner hub: Account + Company Profile + User Profile
import ServicePlans from "./plans/ServicePlans"; // Import the ServicePlans component
import Settings from "./settings/Settings"; // Import the Settings component from new location
import MemberUserInfo_v2 from "./usermgmt/MemberUserInfo_v2"; // Import the MemberUserInfo_v2 component
import MemberActivityView from "./usermgmt/MemberActivityView"; // Member activity monitoring launched from User Management dropdown
import GroupsOrganizationsManagement from "./groupsorganizations/GroupsOrgsMgmt.js"; // Import the Groups & Organizations Management component
import EmailManualScan from "./scamdetector/email/EmailManualScan"; // Import the EmailManualScan component
import EmailAnalysisContainer from "./scamdetector/email/EmailAnalysisContainer"; // Import the EmailAnalysisContainer component
import PhoneNumberScamCheck from "./scamdetector/text/PhoneNumberScamCheck.js"; // Import the PhoneNumberScamCheck component
import TextMessageAnalysis from "./scamdetector/text/TextMessageAnalysis"; // Import the TextMessageAnalysis component
import TextConversationAnalysis from "./scamdetector/text/TextConversationAnalysis"; // Import the TextConversationAnalysis component
import SmsAnalysisSimple from "./scamdetector/text/SmsAnalysisSimple"; // Import the SmsAnalysisSimple component
import SocialMediaAnalysis from "./scamdetector/socialmedia/SocialMediaAnalysis"; // Import the SocialMediaAnalysis component
import URLSafetyAnalysis from "./scamdetector/socialmedia/URLSafetyAnalysis"; // Import the URLSafetyAnalysis component
import ImageAnalysisSimple from "./scamdetector/image/ImageAnalysisSimple"; // Import the ImageAnalysisSimple component
import CreateSimulationSchedule from "./simulation/CreateSimulationSchedule"; // Import the CreateSimulationSchedule component
import ExecuteSimulationSchedule from "./simulation/ExecuteSimulationSchedule"; // Import the ExecuteSimulationSchedule component
import BlogGenerator from "./blogs/BlogGenerator"; // Import the BlogGenerator component
import AdminDashboard from "./adminDashboard/AdminDashboard"; // Import the AdminDashboard component
import AdminScamReports from "./adminDashboard/AdminScamReports"; // Import the Admin Scam Reports component
import UserSubscriptionAudit from "./adminDashboard/components/UserSubscriptionAudit"; // Platform admin: per-user subscription/payment timeline
import AlertManagement from "./AlertManagement.js"; // Import the AlertManagement component
import FeatureComingSoon from "./FeatureComingSoon"; // Import the FeatureComingSoon component
import NotificationSimpleMobile from "./notifications/NotificationSimpleMobile"; // Import the NotificationSimpleMobile component
import HowDoI from "./HowDoI"; // Import the HowDoI component
import GettingStarted from "./GettingStarted"; // Import the GettingStarted component
import ContactSupport from "./ContactSupport"; // Import the ContactSupport component
import ShieldReports from "./reports/ShieldReports"; // Import the Shield Reports component
import CyberInsuranceReport from "./reports/CyberInsuranceReport"; // Corporate cyber-insurance readiness report
import ShieldReportDetail from "./reports/ShieldReportDetail"; // Import the Shield Report Detail component
import AlertsIntelligenceHub from "./alerts/AlertsIntelligenceHub"; // Import the Alerts Intelligence Hub component
import SimulationDataGenerator from "./simulationDataGenerator/SimulationDataGenerator"; // Import the Simulation Data Generator component
import DemoAdminPanel from "./DemoAdminPanel"; // Import the Demo Admin Panel component
import FullAccessDemoAdmin from "./FullAccessDemoAdmin"; // Import the Full-Access Event Demo Seat panel
import TestUserAdmin from "./TestUserAdmin"; // Import the Test User admin panel (browser extension reviewer, etc.)
import EventsAdmin from "./EventsAdmin"; // Import the Events admin console (public events + registrations)

function Hero({
  content,
  onUserSelect,
  selectedUser: propSelectedUser,
  setHeroContent: setHeroContentDirect,
  previousContent,
  goBack,
  navigateWithBack,
  isLoginModalOpen,
  isCreateAccountModalOpen,
  isForgotPasswordModalOpen,
  areAnnouncementsVisible,
}) {
  const {
    loggedInUser,
    isReadOnlyView,
    selectedUser: contextSelectedUser,
  } = useUser(); // Get logged in user and read-only state from UserContext
  const { isDeactivated, isSubscriptionLapsed, isAccountOwner } =
    useFeatureAccess();
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(propSelectedUser);
  const [memberUserInfoSection, setMemberUserInfoSection] = useState(null);
  const heroRef = React.useRef(null);

  // All in-page navigation calls go through navigateWithBack so the back button
  // knows the user arrived via a link click (not a sidebar menu selection).
  const setHeroContent = navigateWithBack || setHeroContentDirect;

  // Update local selectedUser when prop changes
  React.useEffect(() => {
    setSelectedUser(propSelectedUser);
  }, [propSelectedUser]);

  // Scroll hero container to top when content changes
  React.useEffect(() => {
    if (heroRef.current) {
      heroRef.current.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  }, [content]);

  // Note that the case strings below, such as "Simulation Messages" are specified
  // in the SidePanel component and must be matched here.
  // The Hero component is responsible for rendering the
  // selected component based on the content prop passed from the SidePanel.
  const getComponent = () => {
    console.log("Hero.getComponent():  content:", content);
    console.log("Hero.getComponent():  loggedInUser:", loggedInUser);

    // Deactivated members always see the restricted-access message, regardless
    // of what content was requested (e.g. login redirect to "Getting Started").
    if (loggedInUser && isDeactivated) {
      return (
        <div className="hero-welcome-container">
          <h1 className="hero-welcome-title">{t("deactivated.title")}</h1>
          <p className="hero-welcome-subtitle">
            {t("deactivated.heroMessage")}
          </p>
        </div>
      );
    }

    // Lapsed subscription (past the grace period and now inactive).
    // Demo seats and trial users are never "lapsed" (see useFeatureAccess), so
    // their flows are unaffected.
    //  - Account owners may only reach Subscription & Payment, User Profile, and
    //    Settings so they can renew. Any other request is replaced with a notice.
    //  - Non-account-owners are fully restricted: every request shows a notice
    //    telling them to contact the account owner.
    if (loggedInUser && isSubscriptionLapsed) {
      if (!isAccountOwner) {
        return (
          <div className="hero-welcome-container">
            <h1 className="hero-welcome-title">{t("lapsed.title")}</h1>
            <p className="hero-welcome-subtitle">
              {t("lapsed.nonOwner.message")}
            </p>
          </div>
        );
      }

      // Account owner — allow only billing, profile, and settings pages.
      const isOwnerAllowedWhenLapsed =
        typeof content === "string" &&
        (content.startsWith("Account") ||
          content.startsWith("Subscriptions") ||
          content.startsWith("Payments") ||
          content.startsWith("Settings") ||
          content.startsWith("Member User Info"));

      if (!isOwnerAllowedWhenLapsed) {
        return (
          <div className="hero-welcome-container">
            <h1 className="hero-welcome-title">{t("lapsed.title")}</h1>
            <p className="hero-welcome-subtitle">{t("lapsed.owner.message")}</p>
            <button
              type="button"
              className="common-button primary"
              onClick={() => setHeroContent("Account & Billing")}
              style={{ marginTop: 16 }}
            >
              {t("lapsed.owner.cta")}
            </button>
          </div>
        );
      }
      // Allowed page — fall through to the normal switch below.
    }

    // If user is signed in and no specific content is selected, show preferred landing page
    if (loggedInUser && !content) {
      const preferredLandingPage =
        localStorage.getItem("landingPage") || "Dashboard";
      console.log(
        "Hero.getComponent(): User signed in, showing",
        preferredLandingPage,
      );
      if (preferredLandingPage === "Getting Started") {
        return <GettingStarted setHeroContent={setHeroContent} />;
      }
      if (preferredLandingPage === "How do I...?") {
        return <HowDoI setHeroContent={setHeroContent} />;
      }
      return <Dashboard setHeroContent={setHeroContent} />;
    }

    switch (content) {
      case "Simulation Messages":
        return <SimulationHeuristics />;
      case "Simulation Profile":
        return <SimulationProfileSettings setHeroContent={setHeroContent} />;
      case "Recent Simulations":
        return <RecentSimulations />;
      case "Dashboard":
        return <Dashboard setHeroContent={setHeroContent} />;
      case "Reports":
        return <UserEmailReport />;
      case "Generate Email Report":
        return <EmailReportGenerator selectedUser={selectedUser} />;
      case "Generate Risk Scores":
        return <RiskScoresGenerator selectedUser={selectedUser} />;
      case "Send Email":
        return <SendEmail />;
      case "Account Profile":
        return <AccountProfile setHeroContent={setHeroContent} />;
      case "Account Info":
      case "Account & Billing":
      case "Subscriptions":
      case "Payments":
      case "Payments:Methods":
      case "Payments:AddCard":
      case "Payments:History":
        return (
          <SubscriptionPayment
            key={content}
            setHeroContent={setHeroContent}
            initialSection={
              content === "Payments:Methods" || content === "Payments:AddCard"
                ? "payment"
                : content === "Subscriptions"
                  ? "subscription"
                  : content === "Payments:History"
                    ? "history"
                    : undefined
            }
            autoOpenAddCard={content === "Payments:AddCard"}
          />
        );
      case "Settings":
      case "Settings:ChangePassword":
      case "Settings:MFA":
      case "Settings:DisableMFA":
      case "Settings:SmsConsent":
      case "Settings:Notifications":
      case "Settings:Localization":
      case "Settings:Appearance":
        return (
          <Settings
            key={content}
            initialSection={
              content === "Settings:ChangePassword" ||
              content === "Settings:MFA" ||
              content === "Settings:DisableMFA"
                ? "security"
                : content === "Settings:SmsConsent" ||
                    content === "Settings:Notifications"
                  ? "notifications"
                  : content === "Settings:Localization"
                    ? "localization"
                    : content === "Settings:Appearance"
                      ? "appearance"
                      : undefined
            }
            initialAction={
              content === "Settings:ChangePassword"
                ? "changePassword"
                : content === "Settings:MFA"
                  ? "mfa"
                  : content === "Settings:DisableMFA"
                    ? "disableMfa"
                    : undefined
            }
          />
        );
      case "Member User Info":
      case "Member User Info:Create":
      case "Member User Info:Update":
      case "Member User Info:Details":
      case "Member User Info:ResendVerification":
        return (
          <MemberUserInfo_v2
            key={content}
            onUserSelect={onUserSelect}
            setHeroContent={setHeroContent}
            onSectionChange={setMemberUserInfoSection}
            areAnnouncementsVisible={areAnnouncementsVisible}
            initialSection={
              content === "Member User Info:Create"
                ? "create"
                : content === "Member User Info:Update"
                  ? "update"
                  : content === "Member User Info:Details"
                    ? "details"
                    : content === "Member User Info:ResendVerification"
                      ? "resend-verification"
                      : undefined
            }
          />
        );
      case "Member Activity":
        return (
          <MemberActivityView setHeroContent={setHeroContent} goBack={goBack} />
        );
      case "Groups & Organizations":
        return <GroupsOrganizationsManagement />;
      case "Service Plans":
        return <ServicePlans />;
      // Subscriptions and Payments now handled by Account & Billing above
      case "Email Scam Detector Tester":
        return <EmailManualScan setHeroContent={setHeroContent} />;
      case "Scam Detector":
        return <EmailManualScan setHeroContent={setHeroContent} />;
      // New menu items - Email Analysis
      case "Manual Entry": // From Email Analysis submenu
      case "Scan Email Now": // Backward compatibility
        return <EmailManualScan setHeroContent={setHeroContent} />; // Manual email entry
      case "Forwarded Emails": // From Email Analysis submenu
      case "View Forwarded Emails": // Backward compatibility
        return <EmailAnalysisContainer setHeroContent={setHeroContent} />; // Automated email analysis history
      // Backward compatibility - old names
      case "Email Analysis":
        return <EmailAnalysisContainer setHeroContent={setHeroContent} />;
      // Phone Lookup
      case "Check a Phone Number":
      case "Phone Number Check": // Backward compatibility
        return <PhoneNumberScamCheck />;
      case "Phone Lookup":
        return <PhoneNumberScamCheck />;
      // New menu items - Text/SMS Analysis
      case "Manual Input": // From Text/SMS Analysis submenu
      case "Scan Message Now": // Backward compatibility
        return <TextMessageAnalysis />; // Manual text message entry
      case "Text Conversation": // From Text/SMS Analysis submenu
      case "Scan Msg History": // Backward compatibility
        return <TextConversationAnalysis />; // Conversation history analysis
      case "Forwarded Texts": // From Text/SMS Analysis submenu
      case "View Forwarded Msgs": // Backward compatibility
        return <SmsAnalysisSimple setHeroContent={setHeroContent} />; // Automated SMS analysis history
      // Backward compatibility - old names
      case "Text Message Analysis":
        return <TextMessageAnalysis />;
      case "Text/SMS Analysis":
        return <TextMessageAnalysis />;
      // New menu items - Social Media Protection
      case "Business/Person Check":
        return <SocialMediaAnalysis />; // Manual details entry
      // Backward compatibility - old names
      case "Social Media Scams":
        return <SocialMediaAnalysis />;
      case "Social Media Analysis":
        return <SocialMediaAnalysis />;
      // URL Safety Analysis
      case "Social Media Posts":
        return <URLSafetyAnalysis setHeroContent={setHeroContent} />;
      case "Link Safety":
        return <URLSafetyAnalysis setHeroContent={setHeroContent} />;
      case "URL Analysis":
        return <URLSafetyAnalysis setHeroContent={setHeroContent} />;
      // Screenshot / Picture Analysis
      case "Verify a Picture":
      case "Screenshot Analysis": // Backward compatibility
        return <ImageAnalysisSimple />;
      case "Create Simulation Schedule":
        return <CreateSimulationSchedule />;
      case "Execute Simulation Schedule":
        return <ExecuteSimulationSchedule />;
      case "Generate Blogs":
        return <BlogGenerator />;
      case "Manage Announcements":
        return <AlertManagement />;
      case "Admin Dashboard":
        return <AdminDashboard />;
      case "Scam Reports":
        return <AdminScamReports />;
      case "User Subscription Audit":
        return <UserSubscriptionAudit />;
      case "How do I...?":
        return <HowDoI setHeroContent={setHeroContent} />;
      case "Getting Started":
        return <GettingStarted setHeroContent={setHeroContent} />;
      case "ContactSupport":
        return <ContactSupport />;
      case "Notifications":
        return <NotificationSimpleMobile setHeroContent={setHeroContent} />;
      case "Coming Soon":
        return <FeatureComingSoon />;
      // Resources menu items
      case "Training Center":
        return <Training setHeroContent={setHeroContent} />;
      case "Alerts Intelligence Hub":
        return <AlertsIntelligenceHub setHeroContent={setHeroContent} />;
      case "Generate Simulation Data":
        return <SimulationDataGenerator />;
      case "Shield Reports":
        return <ShieldReports setHeroContent={setHeroContent} />;
      case "Cyber-Insurance Readiness":
        return <CyberInsuranceReport />;
      case "Export Data":
        return <FeatureComingSoon featureName="Export Data" />;
      case "Demo Users":
        return <DemoAdminPanel />;
      case "Event Demo Seats":
        return <FullAccessDemoAdmin />;
      case "Test Users":
        return <TestUserAdmin />;
      case "Events":
        return <EventsAdmin />;
      default:
        // Check if it's a "Coming Soon" feature with a specific name
        if (content && content.startsWith("Coming Soon:")) {
          const featureName = content.replace("Coming Soon:", "");
          return <FeatureComingSoon featureName={featureName} />;
        }

        // Check if it's a Shield Report Detail view with report UUID
        if (content && content.startsWith("Shield Report Detail:")) {
          const reportUuid = content.replace("Shield Report Detail:", "");
          return (
            <ShieldReportDetail
              reportUuid={reportUuid}
              setHeroContent={setHeroContent}
            />
          );
        }

        // Check if it's a User Subscription Audit deep-link with prepopulated UUID
        if (content && content.startsWith("User Subscription Audit:UUID:")) {
          const auditUuid = content.substring(
            "User Subscription Audit:UUID:".length,
          );
          return <UserSubscriptionAudit initialUuid={auditUuid} />;
        }

        // If user is signed in, show Dashboard; otherwise show welcome message
        if (loggedInUser) {
          console.log(
            "Hero.getComponent(): Default case - User signed in, showing Dashboard",
          );
          return <Dashboard setHeroContent={setHeroContent} />;
        } else {
          console.log(
            "Hero.getComponent(): Default case - User not signed in, showing welcome message",
          );
          // Don't show welcome message if any modal is open
          if (
            isLoginModalOpen ||
            isCreateAccountModalOpen ||
            isForgotPasswordModalOpen
          ) {
            return <div className="hero-welcome-container"></div>;
          }

          return (
            <div className="hero-welcome-container">
              <h1 className="hero-welcome-title">
                Welcome to the KinShield Security Training Application
              </h1>
              <p className="hero-welcome-subtitle">
                Sign in or Create Account to start protecting yourself and your
                family
              </p>
            </div>
          );
        }
    }
  };

  // Check if we should show the green border (selected user is different from logged in user)
  // Only show for Simulation Profile and specific Member User Info sections (update and details)
  // Also always show when in read-only view (owner viewing member's dashboard/analysis)
  const isSimulationProfile = content === "Simulation Profile";
  const isMemberUserInfoScreen =
    content === "Member User Info" ||
    content === "Member User Info:Create" ||
    content === "Member User Info:Update" ||
    content === "Member User Info:Details" ||
    content === "Member User Info:ResendVerification";
  const isRelevantScreen =
    isSimulationProfile ||
    isMemberUserInfoScreen ||
    content === "Member Activity";
  const showGreenBorder =
    isReadOnlyView ||
    (isRelevantScreen &&
      selectedUser &&
      loggedInUser &&
      selectedUser.uuid !== loggedInUser.uuid);

  return (
    <div
      ref={heroRef}
      className={`hero ${areAnnouncementsVisible ? "with-announcements" : "without-announcements"}`}
      style={showGreenBorder ? { border: "20px solid #10b981" } : {}}
    >
      <BackButton
        previousContent={content === "Member Activity" ? null : previousContent}
        goBack={goBack}
      />
      {getComponent()} {/* Render the corresponding component */}
    </div>
  );
}

export default Hero;
